import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Briefcase, Plus, Search, Copy, Trash2, Award, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Project lifecycle stage shown in the Status column.
// Draft = no round saved yet · Sent = a round has been saved (quote sent) · Completed = outcome marked.
const STAGE_STYLE = {
  Draft: "bg-slate-100 text-slate-700",
  Sent: "bg-sky-100 text-sky-700",
  Completed: "bg-emerald-100 text-emerald-700",
};
function projectStage(p) {
  const oc = p.outcome;
  if (oc && ["won", "lost", "dropped", "partially_awarded"].includes(oc)) return "Completed";
  // "Sent" once a round is saved — at the project level OR on ANY part (parts_with_round_count).
  // Fix: saving each part as Round N1 (and approving parts) now moves the project past Draft.
  if ((p.current_round || 0) > 0 || (p.status && p.status !== "draft") || (p.parts_with_round_count || 0) > 0) return "Sent";
  return "Draft";
}

export default function ProjectList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [custFilter, setCustFilter] = useState(searchParams.get("customer_id") || "");
  const [units, setUnits] = useState([]);
  const [unitFilter, setUnitFilter] = useState(searchParams.get("unit_id") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [outcomeFilter, setOutcomeFilter] = useState(searchParams.get("outcome") || "");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.search = q;
      if (custFilter) params.customer_id = custFilter;
      if (unitFilter && user?.role === "admin") params.unit_id = unitFilter;
      // Status is now a derived stage (Draft/Sent/Completed), filtered client-side below — not sent to the API.
      if (outcomeFilter) params.outcome = outcomeFilter;
      const r = await api.get("/projects", { params });
      setRows(r.data.projects || []);
    } finally {
      setLoading(false);
    }
  };

  // Round 30 — Clone with optional customer override.
  // Opens a small modal so the user can pick a different customer.
  // Defaults to source customer. Customer must exist in master.
  const [cloneModal, setCloneModal] = useState(null); // null or {project, target_customer_id}
  const [cloning, setCloning] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const newMenuRef = useRef(null);

  useEffect(() => {
    if (!newMenuOpen) return;
    const onDown = (e) => {
      if (newMenuRef.current && !newMenuRef.current.contains(e.target)) {
        setNewMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [newMenuOpen]);
  // Round 35 — custom dropdown for clone customer combobox
  const [customerListOpen, setCustomerListOpen] = useState(false);
  const customerComboRef = useRef(null);

  // Round 35 — close customer dropdown when clicking outside
  useEffect(() => {
    if (!customerListOpen) return;
    const onDocClick = (e) => {
      if (customerComboRef.current && !customerComboRef.current.contains(e.target)) {
        setCustomerListOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [customerListOpen]);

  // Round 31 — modal carries a free-text customer name (pre-filled with source customer).
  const openCloneModal = (project) => {
    setCloneModal({ project, target_customer_name: customerName(project.customer_id) || "", target_project_name: project.project_name || "" });
  };

  const confirmClone = async () => {
    if (!cloneModal) return;
    const { project, target_customer_name, target_project_name } = cloneModal;
    const trimmed = (target_customer_name || "").trim();
    const trimmedName = (target_project_name || "").trim();
    if (!trimmed) {
      toast.error("Customer name cannot be empty");
      return;
    }
    if (!trimmedName) {
      toast.error("Project name cannot be empty");
      return;
    }
    setCloning(true);
    try {
      const r = await api.post(`/projects/${project.project_id}/clone`, {
        target_customer_name: trimmed,
        target_project_name: trimmedName,
      });
      toast.success(`Cloned → ${r.data.project_number} (${r.data.children_cloned} parts)`);
      setCloneModal(null);
      nav(`/projects/${r.data.project_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally {
      setCloning(false);
    }
  };

  const deleteProject = async (project) => {
    if (!window.confirm(`Soft-delete ${project.project_number} (${project.project_name})?\n\nAll ${project.child_count || 0} child parts will also be soft-deleted (cascade). The linked RFQ Register entry will also be cascade-deleted if no other live costing references it. Admins can restore everything from the Trash page.`)) return;
    try {
      await api.delete(`/projects/${project.project_id}`);
      toast.success(`Moved ${project.project_number} to Trash`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {});
    }
  }, [user?.role]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, custFilter, unitFilter, statusFilter, outcomeFilter]);

  // Mirror filters into the URL (replace:true) so back-navigation restores them.
  useEffect(() => {
    const p = {};
    if (q) p.q = q;
    if (custFilter) p.customer_id = custFilter;
    if (unitFilter) p.unit_id = unitFilter;
    if (statusFilter) p.status = statusFilter;
    if (outcomeFilter) p.outcome = outcomeFilter;
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line
  }, [q, custFilter, unitFilter, statusFilter, outcomeFilter]);

  const customerName = (id) => customers.find((c) => c.customer_id === id)?.name || "—";
  const hideMargin = user?.role === "npd_engineer";

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="project-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase size={22} className="text-orange-600" /> Projects
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {hideMargin ? "Projects assigned to you." : "Bulk RFQs — group multiple parts under one inquiry."}
          </p>
        </div>
        <div className="relative" ref={newMenuRef}>
          <button
            onClick={() => setNewMenuOpen((o) => !o)}
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-4 py-2"
            data-testid="project-new"
          >
            <Plus size={16} /> New Project <ChevronDown size={14} className={`transition ${newMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {newMenuOpen && (
            <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1" data-testid="project-new-menu">
              <button
                onClick={() => { setNewMenuOpen(false); nav("/projects/new?proto=1"); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2"
                data-testid="project-new-proto"
              >
                <Briefcase size={16} className="mt-0.5 text-orange-600" />
                <div>
                  <div className="text-sm font-medium text-slate-900">Proto Project</div>
                  <div className="text-xs text-slate-500">Prototype — reduced sections</div>
                </div>
              </button>
              <button
                onClick={() => { setNewMenuOpen(false); nav("/projects/new"); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2 border-t border-slate-100"
                data-testid="project-new-series"
              >
                <Briefcase size={16} className="mt-0.5 text-orange-600" />
                <div>
                  <div className="text-sm font-medium text-slate-900">Series Project</div>
                  <div className="text-xs text-slate-500">Full production costing</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className={`p-4 border-b border-slate-200 grid grid-cols-1 md:grid-cols-2 ${user?.role === "admin" ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3`}>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search project, PRJ #…"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="project-search"
            />
          </div>
          <select
            value={custFilter}
            onChange={(e) => setCustFilter(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
          >
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
            ))}
          </select>
          {user?.role === "admin" && (
            <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                    data-testid="project-unit-filter">
              <option value="">All units</option>
              {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_code} — {u.unit_name}</option>)}
            </select>
          )}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                  data-testid="project-status-filter">
            <option value="">All statuses</option>
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
            <option value="Completed">Completed</option>
          </select>
          <select value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                  data-testid="project-outcome-filter">
            <option value="">All outcomes</option>
            <option value="pending">Pending</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="dropped">Dropped</option>
            <option value="partially_awarded">Partially Awarded</option>
          </select>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : (statusFilter ? rows.filter((p) => projectStage(p) === statusFilter) : rows).length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">
            No projects yet. <Link to="/projects/new" className="text-orange-600 hover:text-orange-700">Create one →</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="project-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-3 font-semibold">PRJ #</th>
                  {user?.role === "admin" && <th className="text-left p-3 font-semibold">Unit</th>}
                  <th className="text-left p-3 font-semibold">Project Name</th>
                  <th className="text-left p-3 font-semibold">Customer</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-center p-3 font-semibold">Parts</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(statusFilter ? rows.filter((p) => projectStage(p) === statusFilter) : rows).map((p) => (
                  <tr key={p.project_id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs text-slate-600">
                      <div>{p.project_number}</div>
                      {p.outcome === "won" && (
                        <div className="flex items-center gap-1 text-amber-700 text-[10px] mt-0.5" data-testid={`project-outcome-won-${p.project_number}`}>
                          <Award size={11} /> Won
                        </div>
                      )}
                      {p.outcome === "lost" && (
                        <div className="text-red-700 text-[10px] mt-0.5" data-testid={`project-outcome-lost-${p.project_number}`}>Lost</div>
                      )}
                      {p.outcome === "dropped" && (
                        <div className="text-stone-700 text-[10px] mt-0.5" data-testid={`project-outcome-dropped-${p.project_number}`}>Dropped</div>
                      )}
                      {p.outcome === "partially_awarded" && (
                        <div className="text-purple-700 text-[10px] mt-0.5" data-testid={`project-outcome-partial-${p.project_number}`}>Partial</div>
                      )}
                    </td>
                    {user?.role === "admin" && (
                      <td className="p-3">
                        <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded" data-testid={`project-unit-${p.project_number}`}>
                          {(units.find((un) => un.unit_id === p.unit_id) || {}).unit_code || "—"}
                        </span>
                      </td>
                    )}
                    <td className="p-3">
                      <div className="font-medium text-slate-900 truncate max-w-xs">{p.project_name}</div>
                      <div className="text-xs text-slate-500">{p.end_customer ? `End: ${p.end_customer}` : ""}</div>
                    </td>
                    <td className="p-3 text-slate-700">{customerName(p.customer_id)}</td>
                    <td className="p-3">
                      {(() => {
                        const st = projectStage(p);
                        return (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${STAGE_STYLE[st]}`}>
                            {st}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-3 text-center font-mono text-sm">{p.child_count ?? 0}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => openCloneModal(p)}
                          className="text-slate-400 hover:text-orange-600 transition"
                          title="Clone this project + all parts"
                          data-testid={`project-clone-${p.project_number}`}
                        >
                          <Copy size={15} />
                        </button>
                        {user?.role === "admin" && (
                          <button
                            type="button"
                            onClick={() => deleteProject(p)}
                            className="text-slate-400 hover:text-red-600 transition"
                            title="Soft-delete project + all parts (admin only)"
                            data-testid={`project-delete-${p.project_number}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                        <Link
                          to={`/projects/${p.project_id}`}
                          className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                          data-testid={`project-open-${p.project_number}`}
                        >
                          Open →
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Round 30 — Clone modal with customer override */}
      {cloneModal && (
        <div className="fixed inset-0 bg-slate-900/50 z-40 flex items-center justify-center p-4" data-testid="clone-modal-backdrop">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" data-testid="clone-modal">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Copy size={18} className="text-orange-600" /> Clone project
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Source: <span className="font-mono">{cloneModal.project.project_number}</span> · {cloneModal.project.child_count || 0} parts
              {cloneModal.project.assemblies_count ? ` · ${cloneModal.project.assemblies_count} assemblies` : ""}
            </p>

            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-600 mb-1">Project name for the clone</label>
              <input
                type="text"
                value={cloneModal.target_project_name}
                onChange={(e) => setCloneModal({ ...cloneModal, target_project_name: e.target.value })}
                placeholder="Name for the new project…"
                className="w-full px-2 py-2 border border-slate-300 rounded text-sm bg-white"
                data-testid="clone-project-name"
                disabled={cloning}
              />
              {!(cloneModal.target_project_name || "").trim() && (
                <div className="text-[11px] text-red-600 mt-1">⚠ Project name is required.</div>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-600 mb-1">Customer name for the clone</label>
              <div ref={customerComboRef} className="relative">
                <div className="flex items-stretch">
                  <input
                    type="text"
                    value={cloneModal.target_customer_name}
                    onChange={(e) => setCloneModal({ ...cloneModal, target_customer_name: e.target.value })}
                    placeholder="Pick from list or type a new customer name…"
                    className="w-full px-2 py-2 border border-slate-300 rounded-l rounded-r-none text-sm bg-white border-r-0"
                    data-testid="clone-customer-name"
                    disabled={cloning}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setCustomerListOpen((v) => !v)}
                    className="px-2 border border-slate-300 rounded-r bg-slate-50 hover:bg-slate-100 disabled:opacity-50"
                    disabled={cloning}
                    aria-label="Toggle customer list"
                    data-testid="clone-customer-dropdown-toggle"
                  >
                    <ChevronDown size={16} className={`text-slate-600 transition ${customerListOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {customerListOpen && (
                  <ul
                    className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg text-sm"
                    data-testid="clone-customer-list"
                  >
                    {customers.length === 0 && (
                      <li className="px-3 py-2 text-slate-400 italic">No customers in master.</li>
                    )}
                    {customers.map((c) => (
                      <li
                        key={c.customer_id}
                        onClick={() => {
                          setCloneModal({ ...cloneModal, target_customer_name: c.name });
                          setCustomerListOpen(false);
                        }}
                        className="px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-slate-800"
                      >
                        {c.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {(() => {
                const typed = (cloneModal.target_customer_name || "").trim();
                const sourceName = customerName(cloneModal.project.customer_id) || "";
                if (!typed) {
                  return <div className="text-[11px] text-red-600 mt-1">⚠ Customer name is required.</div>;
                }
                if (typed.toLowerCase() === sourceName.toLowerCase()) {
                  return <div className="text-[11px] text-slate-500 mt-1">Same as source customer — no change.</div>;
                }
                const existsInMaster = customers.some(
                  (c) => (c.name || "").trim().toLowerCase() === typed.toLowerCase()
                );
                if (existsInMaster) {
                  return (
                    <div className="text-[11px] text-amber-700 mt-1">
                      ⚠ Customer will change from "{sourceName}" to "{typed}" (already in master) on the cloned project + all parts.
                    </div>
                  );
                }
                return (
                  <div className="text-[11px] text-amber-700 mt-1">
                    ⚠ "{typed}" is NOT in the customer master — a new customer record will be auto-created and the clone + all parts will use it.
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setCloneModal(null)}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded text-slate-700 hover:bg-slate-50"
                disabled={cloning}
                data-testid="clone-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmClone}
                className="px-4 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded font-semibold disabled:bg-orange-300"
                disabled={cloning || !(cloneModal.target_customer_name || "").trim() || !(cloneModal.target_project_name || "").trim()}
                data-testid="clone-confirm"
              >
                {cloning ? "Cloning…" : "Clone"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
